import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronLeft, Mail, Phone, Search } from "lucide-react";
import { normalizeLabel, suggestedLabels, type Contact } from "@omina/core";
import {
  useClearEnrichment,
  useContactAvatars,
  useContacts,
  useEnrichContact,
  useMoves,
  useOutcomes,
  useHeadlines,
  useProfiles,
  useSettings,
  useUpdateContact,
} from "../lib/queries";
import { FACT_INK, Face, Fact, LinkedInMark } from "../components/circle/PersonCard";

/**
 * One person, in full.
 *
 * Everything the product knows about somebody, on the screen she reaches by
 * tapping their row — and nothing it does not know. The three rules this
 * surface keeps:
 *
 *  1. **Research is a decision, not a side effect.** Nothing is searched until
 *     she presses the button on this page. Drafting a follow-up used to search
 *     silently whenever nothing was cached, which spent money and wrote notes
 *     about a person she never asked us to look up.
 *  2. **A missing fact is missing, not blank.** No empty rows with dashes in
 *     them. Most people have almost no public footprint and a card that says
 *     so is correct.
 *  3. **Her words are marked as hers.** The note block is visually separate
 *     from everything found on the web, because in three months the difference
 *     between "she wrote this" and "we found this" is the whole value.
 */
export const Route = createFileRoute("/person/$id")({
  component: Person,
});

const STATE_LABEL: Record<string, { label: string; color: string }> = {
  new: { label: "new", color: "var(--muted-foreground)" },
  contacted: { label: "sent", color: "var(--auth)" },
  replied: { label: "replied", color: "var(--cust)" },
  met: { label: "met", color: "var(--cust)" },
  closed: { label: "closed", color: "var(--fund)" },
};

function fitInk(score: number): string {
  if (score >= 70) return "var(--cust)";
  if (score >= 40) return "var(--auth)";
  return "var(--muted-foreground)";
}

const GOAL_WORD: Record<string, string> = {
  cust: "customers",
  auth: "authority",
  fund: "funding",
};


/**
 * The three dimensions, in the order she reads them.
 *
 * `network` carries a caveat rather than a promise: Omina has no social graph
 * — rule #5 means it never sees anybody's connections but hers — so this is an
 * inference from where a person sits, not a lookup of who they know. Saying so
 * on the card is the difference between a useful signal and a lie about what
 * the product can see.
 */
const SIGNALS = [
  {
    key: "company" as const,
    label: "Works somewhere that matters",
    hint: null as string | null,
  },
  {
    key: "knowledge" as const,
    label: "Knows what you need",
    hint: null as string | null,
  },
  {
    key: "network" as const,
    label: "Can open doors",
    hint: "Inferred from their role — Omina cannot see anyone's connections.",
  },
];

function Person() {
  const { id } = Route.useParams();
  const contacts = useContacts();
  const avatars = useContactAvatars(true);
  const profiles = useProfiles(true);
  const headlines = useHeadlines(true);
  const settings = useSettings();

  const contact = contacts.data?.find((c) => c.id === id);

  if (contacts.isLoading) return <p className="lede">Reading your circle…</p>;
  if (!contact) {
    return (
      <>
        <h1 className="tk">Not in your circle.</h1>
        <p className="lede">
          This person isn't here — they may have been removed.{" "}
          <Link to="/circle">Back to your circle</Link>.
        </p>
      </>
    );
  }

  return (
    <Detail
      contact={contact}
      avatarUrl={avatars.data?.[contact.id]}
      profileUrl={profiles.data?.[contact.id]}
      headline={headlines.data?.[contact.id]}
      activeGoal={settings.data?.activeGoal}
    />
  );
}

function Detail({
  contact,
  avatarUrl,
  profileUrl,
  headline,
  activeGoal,
}: {
  contact: Contact;
  avatarUrl?: string;
  profileUrl?: string;
  headline?: string;
  activeGoal?: string | null;
}) {
  const enrich = useEnrichContact();
  const clear = useClearEnrichment();
  const update = useUpdateContact();
  const moves = useMoves();
  const outcomes = useOutcomes();

  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(contact.context);

  const found = contact.enrichment;
  const state = STATE_LABEL[contact.state] ?? STATE_LABEL.new!;
  const where =
    [contact.role ?? found?.currentRole, contact.company ?? found?.currentCompany]
      .filter(Boolean)
      .join(" @ ") || headline;

  const email = contact.email ?? found?.contactInfo?.email;
  const phone = found?.contactInfo?.phone;
  /** What she recorded wins over what the web guessed — the one she typed or
   *  the invitation carried is the one she has actually seen. */
  const linkedin = contact.linkedinUrl ?? found?.contactInfo?.linkedin ?? profileUrl;

  const researched = Boolean(contact.researchedAt);
  const age = contact.researchedAt
    ? Math.floor((Date.now() - new Date(contact.researchedAt).getTime()) / 86_400_000)
    : null;

  /** Her follow-ups to this person, newest first. */
  const activity = (moves.data ?? [])
    .filter((m) => m.contactId === contact.id)
    .slice(0, 6)
    .map((m) => ({
      id: m.id,
      what: m.channel === "email" ? "Sent a follow-up" : `Logged a ${m.channel}`,
      when: m.sentAt ?? null,
      outcome: outcomes.data?.find((o) => o.moveId === m.id)?.result,
    }));

  return (
    <>
      <div className="flex items-center justify-between">
        <Link to="/circle" className="linkbtn" aria-label="Back to your circle">
          <ChevronLeft size={18} aria-hidden="true" /> Circle
        </Link>
        <span className="ppill" style={{ color: state.color }}>
          {state.label}
        </span>
      </div>

      <div className="phero mt-5.5">
        <Face seed={contact.id} name={contact.name} imageUrl={avatarUrl} />
        <div className="pbody">
          <div className="pname">{contact.name}</div>
          {where && <div className="pwhere">{where}</div>}
        </div>
      </div>

      {/*
        Write and Call appear only when there is an address or a number — a
        greyed-out button for something we do not have is a promise the card
        cannot keep. Draft and Research need neither, so they are always here:
        a button she has to scroll to find is a button that does not exist.
      */}
      <div className="pacts">
        {email && (
          <a className="pact" href={`mailto:${email}`}>
            <Mail size={15} style={{ color: FACT_INK.mail }} aria-hidden="true" /> Write
          </a>
        )}
        {phone && (
          <a className="pact" href={`tel:${phone}`}>
            <Phone size={15} style={{ color: FACT_INK.phone }} aria-hidden="true" /> Call
          </a>
        )}
        {linkedin && (
          <a className="pact" href={linkedin} target="_blank" rel="noreferrer noopener">
            <span style={{ color: FACT_INK.linkedin, display: "inline-flex" }}>
              <LinkedInMark />
            </span>
            Profile
          </a>
        )}
        <Link
          to="/followup"
          search={{ contact: contact.id }}
          className="pact"
          style={{ textDecoration: "none" }}
        >
          Draft follow-up
        </Link>
        <button
          type="button"
          className="pact"
          disabled={enrich.isPending}
          onClick={() => enrich.mutate({ id: contact.id, force: researched })}
        >
          <Search size={15} style={{ color: FACT_INK.place }} aria-hidden="true" />
          {enrich.isPending ? "Searching…" : researched ? "Search again" : "Research"}
        </button>
      </div>

      {(email || phone || linkedin || contact.metAt?.eventTitle) && (
        <div className="psection">
          <div className="pstitle">Contact information</div>
          {email && <Fact icon="mail" label="Email" value={email} href={`mailto:${email}`} ink={FACT_INK.mail} />}
          {phone && <Fact icon="phone" label="Phone" value={phone} href={`tel:${phone}`} ink={FACT_INK.phone} />}
          {linkedin && (
            <Fact
              icon="linkedin"
              label="LinkedIn"
              // The handle, not the tracking-laden url — it is what she would
              // recognise, and the row has one line to say it in.
              value={linkedin.replace(/^https?:\/\/(www\.)?linkedin\.com\/(in\/)?/i, "").replace(/\/$/, "")}
              href={linkedin}
              ink={FACT_INK.linkedin}
            />
          )}
          {contact.metAt?.eventTitle && (
            <Fact
              icon="place"
              label={contact.metAt.eventDate ? `Met · ${contact.metAt.eventDate}` : "Met at"}
              value={contact.metAt.eventLocation || contact.metAt.eventTitle}
              ink={FACT_INK.place}
            />
          )}
        </div>
      )}

      <div className="psection">
        <div className="flex items-center justify-between">
          <div className="pstitle">Your notes</div>
          <button type="button" className="linkbtn" onClick={() => setEditing((e) => !e)}>
            {editing ? "Cancel" : contact.context ? "Edit" : "Add"}
          </button>
        </div>

        {editing ? (
          <div className="field">
            <textarea
              className="tk"
              rows={4}
              aria-label={`What to remember about ${contact.name}`}
              placeholder="What you talked about, what they need, what to do next…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="actions">
              <button
                type="button"
                className="btn small"
                disabled={update.isPending}
                onClick={() => {
                  update.mutate({ id: contact.id, context: note });
                  setEditing(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        ) : contact.context ? (
          <div className="pnote">{contact.context}</div>
        ) : (
          <p className="hint">Nothing yet — what should you remember about them?</p>
        )}
      </div>

      {/*
        Research, and the reason it is a button rather than something that has
        already happened: it reaches out to the public web about a named
        person and it costs a search. That is hers to decide, one person at a
        time (rule #7 — never in bulk).
      */}
      <div className="psection">
        <div className="pstitle">Research</div>

        {found?.goalFit ? (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <b style={{ color: fitInk(found.goalFit.score) }}>{found.goalFit.score}%</b>
              <span className="itype">
                match for {GOAL_WORD[found.goalFit.goal] ?? found.goalFit.goal}
              </span>
              {activeGoal && found.goalFit.goal !== activeGoal && (
                <span className="itype">· scored for your previous goal</span>
              )}
            </div>
            <div className="fitbar mt-1.5">
              <div
                className="fitbarfill"
                style={{
                  width: `${found.goalFit.score}%`,
                  background: fitInk(found.goalFit.score),
                }}
              />
            </div>
            {found.goalFit.reasons.map((why) => (
              <div key={why} className="why mt-1.5">
                • {why}
              </div>
            ))}

            {/*
              The three ways somebody moves a goal forward, each with the
              sentence that earned it.

              All three bars take their colour from the score, not from the
              dimension. The meter hues are spoken for — petrol means
              customers, gold authority, ember funding — and borrowing them to
              mean "network" and "knowledge" would make the same colour say two
              different things across the product. The label distinguishes
              them; the colour says how strong.

              A dimension with no evidence is absent, never a zero bar.
            */}
            {found.goalFit.signals && (
              <div className="mt-3">
                {SIGNALS.map(({ key, label, hint }) => {
                  const signal = found.goalFit!.signals?.[key];
                  if (!signal) return null;
                  return (
                    <div key={key} className="mt-2.5">
                      <div className="flex items-center gap-2">
                        <b style={{ color: fitInk(signal.score) }}>{signal.score}%</b>
                        <span className="itype">{label}</span>
                      </div>
                      <div className="fitbar mt-1">
                        <div
                          className="fitbarfill"
                          style={{
                            width: `${signal.score}%`,
                            background: fitInk(signal.score),
                          }}
                        />
                      </div>
                      <div className="why mt-1">{signal.why}</div>
                      {hint && <div className="hint">{hint}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : researched ? (
          <p className="hint">Not scored — your goal has no named targets to score against yet.</p>
        ) : null}

        {researched && found && (
          <div className="samplebar">
            {found.milestones?.map((m) => (
              <div key={m.what}>
                • {m.what}
                {m.when ? ` (${m.when})` : ""}
              </div>
            ))}
            {found.previousRoles?.map((r) => (
              <div key={`${r.role}-${r.company}`}>
                • Previously {r.role} at {r.company}
                {r.when ? ` (${r.when})` : ""}
              </div>
            ))}
            {found.education?.length ? <div>• {found.education.join(", ")}</div> : null}
            {found.investorProfile?.thesis && <div>• Invests in: {found.investorProfile.thesis}</div>}

            <div className="hint">
              {found.sources.slice(0, 4).map((url, i) => (
                <span key={url}>
                  {i > 0 ? " · " : ""}
                  <a href={url} target="_blank" rel="noreferrer noopener">
                    source {i + 1}
                  </a>
                </span>
              ))}
              {age !== null && <> · found {age === 0 ? "today" : `${age}d ago`}</>}
            </div>
          </div>
        )}

        {/* An empty result is a real answer, and saying so is what stops her
            pressing the button again expecting a different web. */}
        {researched && !found?.milestones?.length && !found?.previousRoles?.length && (
          <p className="hint">Nothing public we could tie to them with confidence.</p>
        )}

        {/* One trigger, up in the action row. What stays here is the undo:
            a wrong match is the failure this research can actually have, and
            she needs to be able to say so next to what it found. */}
        {researched && (
          <div className="chips mt-1.5">
            <button
              type="button"
              className="chip"
              disabled={clear.isPending}
              onClick={() => clear.mutate({ id: contact.id })}
            >
              This isn't them
            </button>
          </div>
        )}

        {!researched && (
          <p className="hint">
            Searches the public web for this one person. Nothing is looked up until you ask.
          </p>
        )}
        {/* Our failure, said as ours. The API answers 503 with a sentence and
            stamps nothing, so pressing again in a minute is the right move
            and the card no longer claims the web has never heard of them. */}
        {enrich.isError && <div className="danger">{enrich.error.message}</div>}
        {!researched && !enrich.isError && enrich.isSuccess && (
          <p className="hint">Nothing public we could tie to them with confidence.</p>
        )}
      </div>

      <div className="psection">
        <div className="pstitle">Labels</div>
        <Labels contact={contact} />
      </div>

      {activity.length > 0 && (
        <div className="psection">
          <div className="pstitle">Recent activity</div>
          {activity.map((a) => (
            <div key={a.id} className="prow">
              <span className="picon" style={{ background: FACT_INK.mail }}>
                <Mail size={15} aria-hidden="true" />
              </span>
              <span className="pval">
                <span style={{ display: "block" }}>{a.what}</span>
                <span className="plabel">
                  {a.when ? new Date(a.when).toLocaleDateString() : "not sent yet"}
                  {a.outcome ? ` · ${a.outcome.replace(/_/g, " ")}` : ""}
                </span>
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Filing somebody, so she can find them by the thing she remembers.
 *
 * Suggestions are derived from what is already on the record — the room she
 * confirmed, the role the person publishes — never inferred about someone from
 * someone else. They are dashed until applied, because a proposal that looks
 * like a fact is the failure this product keeps refusing to ship.
 */
function Labels({ contact }: { contact: Contact }) {
  const update = useUpdateContact();
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");

  const applied = contact.labels ?? [];
  const suggestions = suggestedLabels(contact).slice(0, 3);

  function set(next: string[]) {
    update.mutate({ id: contact.id, labels: next });
  }

  return (
    <div className="chips">
      {applied.map((label) => (
        <span key={label} className="chip inline-flex gap-1.5">
          {label.replace(/-/g, " ")}
          <button
            type="button"
            className="linkbtn"
            aria-label={`Remove ${label}`}
            onClick={() => set(applied.filter((l) => l !== label))}
          >
            ×
          </button>
        </span>
      ))}

      {suggestions.map((label) => (
        <button
          key={label}
          type="button"
          className="chip suggested"
          onClick={() => set([...applied, label])}
        >
          + {label.replace(/-/g, " ")}
        </button>
      ))}

      {adding ? (
        <input
          className="tk w-45 h-7.5 px-2.5 py-1 text-xs"
          aria-label="New label"
          placeholder="label"
          value={text}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const next = normalizeLabel(text);
            if (next && !applied.includes(next)) set([...applied, next]);
            setText("");
            setAdding(false);
          }}
        />
      ) : (
        <button type="button" className="linkbtn" onClick={() => setAdding(true)}>
          + label
        </button>
      )}
    </div>
  );
}
